import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import {
  Brain,
  Zap,
  Target,
  TrendingUp,
  Users,
  ArrowRight,
  Sparkles,
  Shield
} from 'lucide-react'

const features = [
  {
    icon: Brain,
    title: 'AI-Powered Validation',
    description: '5 AI validators review every market for clarity, verifiability, and fairness before going live.',
    color: 'text-primary-600 bg-primary-100'
  },
  {
    icon: Zap,
    title: 'Comprehensive Tool Access',
    description: '50+ API integrations including crypto, social media, finance, sports, news, and government data.',
    color: 'text-yellow-600 bg-yellow-100'
  },
  {
    icon: Target,
    title: 'Smart Resolution',
    description: 'AIs research using real data sources and an Adjudicator makes final decisions with full transparency.',
    color: 'text-green-600 bg-green-100'
  },
  {
    icon: Users,
    title: 'Creator Economy',
    description: 'Market creators earn 0.5% of all betting volume. Build successful markets and get rewarded.',
    color: 'text-purple-600 bg-purple-100'
  }
]

const examples = [
  {
    question: 'Will Bitcoin exceed $130,000 by Dec 31, 2025?',
    category: 'Crypto',
    route: 'Multiple price feeds',
    odds: '52¢ YES / 48¢ NO'
  },
  {
    question: 'Will Taylor Swift release a new album this year?',
    category: 'Entertainment',
    route: 'Social media + news',
    odds: '73¢ YES / 27¢ NO'
  },
  {
    question: 'Will Apple stock outperform Tesla this quarter?',
    category: 'Finance',
    route: 'Stock market APIs',
    odds: '61¢ YES / 39¢ NO'
  }
]

export default function HomePage() {
  const navigate = useNavigate()
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center justify-center space-x-2 mb-6">
                <div className="flex items-center space-x-2 bg-[#052393]/20 border border-[#2AAAEA]/30 px-4 py-2 rounded-full">
                  <Sparkles className="w-4 h-4 text-cpx-light-blue" />
                  <span className="text-sm font-bold text-cpx-light-blue uppercase tracking-wider">
                    Creator-Driven Prediction Exchanges
                  </span>
                </div>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
                Make YOUR Pool.
                <br />
                <span className="text-gradient">Powered by AI. Driven by YOU.</span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                The only prediction market that lets YOU make the pool. Create markets, set the terms, 
                and earn from every trade — all validated and resolved by AI with 50+ real-time data sources.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 flex-wrap">
                <Link
                  to="/create"
                  className="btn-primary text-lg px-8 py-4 flex items-center space-x-2 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                >
                  <span>Create Prediction Market</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/fantasy/create"
                  className="text-lg px-8 py-4 flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                >
                  <span>Create Fantasy Contest</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/markets"
                  className="btn-secondary text-lg px-8 py-4 flex items-center space-x-2"
                >
                  <TrendingUp className="w-5 h-5" />
                  <span>Browse Markets</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Hero Background Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-[#2AAAEA]/20 rounded-full opacity-20 blur-xl animate-pulse-soft" />
        <div className="absolute top-40 right-16 w-32 h-32 bg-[#009245]/20 rounded-full opacity-20 blur-xl animate-pulse-soft" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-[#FBB03B]/20 rounded-full opacity-20 blur-xl animate-pulse-soft" style={{ animationDelay: '2s' }} />
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-black text-white mb-4">
                Why Choose CPX?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Traditional prediction markets rely on human judgment and manual resolution. 
                CPX uses AI to make markets faster, more accurate, and completely transparent — while YOU control the pool.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="card p-8 hover:shadow-lg hover:shadow-[#2AAAEA]/10 hover:border-[#2AAAEA]/40 transition-all"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#2AAAEA] to-[#052393] rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-[#2AAAEA]/30">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{feature.title}</h3>
                <p className="text-gray-300 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Markets */}
      <section className="py-20 bg-[#001233]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Example Markets
            </h2>
            <p className="text-xl text-gray-400">
              See the kinds of markets you can create and trade on
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {examples.map((market, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card p-6 hover:shadow-lg hover:shadow-[#2AAAEA]/10 hover:border-[#2AAAEA]/40 transition-all"
              >
                <div className="flex items-center space-x-2 mb-4">
                  <div className="px-2 py-1 rounded text-xs font-bold bg-[#052393]/40 text-cpx-light-blue uppercase tracking-wider">{market.category}</div>
                  <div className="px-2 py-1 rounded text-xs font-bold bg-[#009245]/20 text-cpx-green flex items-center">
                    <Brain className="w-3 h-3 mr-1" />
                    AI VERIFIED
                  </div>
                </div>
                
                <h3 className="font-bold text-white mb-3 leading-tight">
                  {market.question}
                </h3>
                
                <div className="text-sm text-gray-400 mb-4">
                  <div className="flex items-center space-x-1 mb-1">
                    <Zap className="w-3 h-3 text-cpx-yellow" />
                    <span>Route: {market.route}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="font-bold text-white">{market.odds}</span>
                  <button className="text-cpx-light-blue hover:text-glow-aqua font-bold">
                    View Market →
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-300">
              Simple for users, sophisticated behind the scenes
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Create & Bet</h3>
              <p className="text-gray-300 leading-relaxed">
                Create your market, choose a verification route, and bet on your own pool. 
                5 AIs review for approval.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-gradient-success rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Trade & Earn</h3>
              <p className="text-gray-300 leading-relaxed">
                Users bet on outcomes, you earn creator fees. 
                All activity is tracked transparently on-chain.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-gradient-danger rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Auto-Resolve</h3>
              <p className="text-gray-300 leading-relaxed">
                AIs research the outcome using real data sources. 
                Winners get paid automatically.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#2AAAEA] to-[#052393]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <h2 className="text-4xl font-black mb-4">
              Ready to Make YOUR Pool?
            </h2>
            <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto font-medium">
              Join CPX. Create verifiable, AI-resolved markets on YOUR terms 
              and start earning from day one.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link
                to="/create"
                className="bg-white text-[#052393] hover:bg-gray-100 px-8 py-4 rounded-xl font-black text-lg flex items-center space-x-2 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all uppercase tracking-wide"
              >
                <span>Get Started Now</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <button
                onClick={() => navigate('/markets')}
                className="relative z-10 text-white border-2 border-white/30 hover:border-white/50 hover:bg-white/10 px-8 py-4 rounded-xl font-bold text-lg flex items-center space-x-2 transition-all cursor-pointer"
              >
                <TrendingUp className="w-5 h-5" />
                <span>Browse Markets</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
